import {
  type ImagePickerAsset,
  type ImagePickerOptions,
  launchImageLibraryAsync,
} from "expo-image-picker";
import { useCallback, useState } from "react";

export type UseImagePickerOptions = {
  /**
   * Fires once the user finishes selecting images. Return a `Promise` to keep
   * `isLoading` true until your upload work settles; the hook awaits it inside
   * its own `try/finally` and clears loading when the promise resolves.
   */
  onImageSelected: (assets: ImagePickerAsset[]) => Promise<void> | void;
};

export type UseImagePickerResult = {
  /** Opens the OS image library; resolves once the picker is dismissed. */
  pickImage: () => Promise<void>;
  /** True from `pickImage` invocation until `onImageSelected` settles. */
  isLoading: boolean;
};

const imagePickerOptions: ImagePickerOptions = {
  allowsEditing: false,
  mediaTypes: ["images"],
  allowsMultipleSelection: true,
  // Default compression
  // 0 means compress for small size, 1 means compress for maximum quality
  quality: 0.8,
};

/**
 * Wraps `expo-image-picker`'s `launchImageLibraryAsync`. Owns its own
 * `isLoading` state and awaits the consumer's `onImageSelected` callback so
 * the loading affordance stays up while uploads complete.
 */
export const useImagePicker = ({
  onImageSelected,
}: UseImagePickerOptions): UseImagePickerResult => {
  const [isLoading, setIsLoading] = useState(false);

  const pickImage = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await launchImageLibraryAsync(imagePickerOptions);
      if (result.canceled) {
        return;
      }
      await onImageSelected(result.assets);
    } catch (error) {
      console.error("Error picking image", error);
    } finally {
      setIsLoading(false);
    }
  }, [onImageSelected]);

  return { pickImage, isLoading };
};
